// CanalesMercadoLibre — /admin/canales/mercadolibre
// Detalle del canal ML: KPIs + tabla de productos publicables con toggle + markup editable
// + cálculo automático precio ML + comisión + neto.
// Lee de la vista ml_productos_publicables.

import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

export default function CanalesMercadoLibre() {
  const [loading, setLoading] = useState(true)
  const [productos, setProductos] = useState([])
  const [productosAll, setProductosAll] = useState([])  // todos los productos para el dropdown "Agregar"
  const [config, setConfig] = useState({
    markup_default: 28,
    comision_pct: 13.5,
    costo_fijo_rangos: [{ hasta: 500, fijo: 15 }, { hasta: 750, fijo: 25 }, { hasta: 1000, fijo: 40 }],
  })
  const [stats, setStats] = useState({
    items_publicados: 0,
    pubs_sin_vincular: 0, ventas_sin_mapear: 0,
    // Ventas
    ventas_7d: 0, ventas_7d_prev: 0, pedidos_7d: 0,
    ventas_30d: 0, pedidos_30d: 0, ticket_30d: 0,
    // Operativo
    pendientes_despacho: 0, preguntas_urgentes: 0, cfe_pendientes: 0,
    // Contable
    comision_30d: 0, costo_fijo_30d: 0, credito_envio_30d: 0, neto_30d: 0,
    preguntas_pendientes: 0,
    credencial_conectada: false,
  })
  const [filtro, setFiltro] = useState({ texto: '', estado: 'todos' })
  const [msg, setMsg] = useState(null)
  const [showAgregar, setShowAgregar] = useState(false)

  const [reprocesando, setReprocesando] = useState(false)
  const [pendientesSync, setPendientesSync] = useState(0)

  // Sprint 5 — salud de cuenta, ranking top del mes, publicaciones con error
  const [salud, setSalud] = useState(null)
  const [ranking, setRanking] = useState([])
  const [pubsError, setPubsError] = useState([])

  async function cargar() {
    setLoading(true)
    const now = Date.now()
    const hace7  = new Date(now -  7 * 86400000).toISOString()
    const hace14 = new Date(now - 14 * 86400000).toISOString()
    const hace30 = new Date(now - 30 * 86400000).toISOString()
    const hace2h = new Date(now - 2 * 3600000).toISOString()
    const hace24h = new Date(now - 24 * 3600000).toISOString()
    const [prodRes, prodAllRes, confRes, pubsRes, ventas30dRes, pregsRes, credRes, pendRes, urgentesRes, pregsAnsRes, reclamosRes, pubsErrorRes, pubsSinVincularRes, ventasSinMapearRes] = await Promise.all([
      supabase.from('ml_productos_publicables').select('*, ml_stats, ml_stats_actualizado_at').order('nombre'),
      supabase.from('productos').select('id, sku, nombre, marca, presentacion, precio_publico, stock_actual, stock_reservado, publicar_en_ml').is('deleted_at', null).eq('activo', true).order('nombre'),
      supabase.from('app_config').select('key, value').like('key', 'ml_%'),
      supabase.from('ml_publicaciones').select('id', { count: 'exact', head: true }).eq('status', 'active'),
      supabase.from('ml_ventas')
        .select('monto_bruto, comision_monto, costo_fijo, neto, neto_real, envio_compensacion, status, date_shipped, cfe_id, date_created, producto_id, cantidad')
        .gte('date_created', hace30),
      supabase.from('ml_preguntas').select('id', { count: 'exact', head: true }).eq('status', 'unanswered'),
      supabase.from('ml_credentials').select('access_token, nickname, user_id_ml').maybeSingle(),
      supabase.from('ml_publicaciones').select('id', { count: 'exact', head: true }).not('pendiente_sync_ml', 'is', null),
      supabase.from('ml_preguntas').select('id', { count: 'exact', head: true }).eq('status', 'unanswered').lt('date_created', hace2h),
      // Sprint 5.1 — preguntas respondidas 30d (para tiempo medio de respuesta)
      supabase.from('ml_preguntas').select('date_created, answered_at').not('answered_at', 'is', null).gte('answered_at', hace30),
      // Sprint 5.1 — reclamos (para % abiertos)
      supabase.from('ml_reclamos').select('id, resuelto_at, reason_name, ml_order_id, date_created').order('date_created', { ascending: false }).limit(200),
      // Sprint 5.3 — publicaciones con error o sync trabado >24h
      supabase.from('ml_publicaciones').select('id, titulo, producto_id, status, pendiente_sync_motivo, pendiente_sync_desde')
        .or(`status.eq.error,pendiente_sync_desde.lt.${hace24h}`).limit(50),
      // Quick win 2026-08-14 — publicaciones activas sin producto vinculado (rompe facturación + reportes por producto)
      supabase.from('ml_publicaciones').select('id', { count: 'exact', head: true }).eq('status', 'active').is('producto_id', null),
      // Quick win 2026-08-14 — ventas del mes sin producto mapeado (bloquean CFE por IVA_NO_RESUELTO)
      supabase.from('ml_ventas').select('id', { count: 'exact', head: true }).is('producto_id', null).gte('date_created', hace30),
    ])
    setPendientesSync(pendRes.count || 0)

    const cfg = {}
    for (const r of confRes.data || []) {
      const v = r.value
      cfg[r.key] = typeof v === 'string' ? v.replace(/^"|"$/g, '') : v
    }
    setConfig({
      markup_default:       Number(cfg.ml_markup_default_pct ?? 28),
      comision_pct:         Number(cfg.ml_comision_pct ?? 13.5),
      costo_fijo_rangos:    Array.isArray(cfg.ml_costo_fijo_rangos)
                              ? cfg.ml_costo_fijo_rangos
                              : [{ hasta: 500, fijo: 15 }, { hasta: 750, fijo: 25 }, { hasta: 1000, fijo: 40 }],
    })

    setProductos(prodRes.data || [])
    setProductosAll(prodAllRes.data || [])

    const ventas30 = ventas30dRes.data || []
    // Facturables = lo que realmente cuenta como venta (paid/shipped/delivered). Excluye canceladas y
    // pendiente_pago. MISMO criterio que la página Ventas -> los KPIs de plata coinciden entre las dos.
    const pagadas    = ventas30.filter(v => ['paid', 'shipped', 'delivered'].includes(v.status))
    const v7  = pagadas.filter(v => v.date_created >= hace7)
    const v14 = pagadas.filter(v => v.date_created >= hace14 && v.date_created < hace7)
    const bruto30    = pagadas.reduce((s, v) => s + Number(v.monto_bruto || 0), 0)
    const comision30 = pagadas.reduce((s, v) => s + Number(v.comision_monto || 0), 0)
    const costoFijo30= pagadas.reduce((s, v) => s + Number(v.costo_fijo || 0), 0)
    const credito30  = pagadas.reduce((s, v) => s + Number(v.envio_compensacion || 0), 0) // crédito envío Flex
    const neto30     = pagadas.reduce((s, v) => s + Number(v.neto_real ?? v.neto ?? 0), 0) // neto REAL (incluye crédito envío)
    const pendDespacho = pagadas.filter(v => !v.date_shipped).length
    const cfePendientes = pagadas.filter(v => !v.cfe_id).length

    setStats({
      items_publicados: pubsRes.count || 0,
      pubs_sin_vincular: pubsSinVincularRes.count || 0,
      ventas_sin_mapear: ventasSinMapearRes.count || 0,
      // Ventas
      ventas_7d:  v7.reduce((s, v) => s + Number(v.monto_bruto || 0), 0),
      pedidos_7d: v7.length,
      ventas_7d_prev: v14.reduce((s, v) => s + Number(v.monto_bruto || 0), 0),
      ventas_30d: bruto30, pedidos_30d: ventas30.length,
      ticket_30d: ventas30.length ? Math.round(bruto30 / ventas30.length) : 0,
      // Operativo
      pendientes_despacho: pendDespacho,
      preguntas_urgentes: urgentesRes.count || 0,
      cfe_pendientes: cfePendientes,
      // Contable
      comision_30d: comision30, costo_fijo_30d: costoFijo30, credito_envio_30d: credito30, neto_30d: neto30,
      preguntas_pendientes: pregsRes.count || 0,
      credencial_conectada: !!credRes.data?.access_token,
      nickname: credRes.data?.nickname,
    })

    // ── Sprint 5.1 · salud de cuenta ──────────────────────────────────────
    // Tiempo medio de respuesta (hs) sobre preguntas respondidas en 30d.
    const pAns = (pregsAnsRes.data || []).filter(p => p.answered_at && p.date_created)
    const tiempos = pAns.map(p => (new Date(p.answered_at) - new Date(p.date_created)) / 3600000).filter(h => h >= 0)
    const tiempoResp = tiempos.length ? tiempos.reduce((a, b) => a + b, 0) / tiempos.length : null
    // Reclamos abiertos (sin resuelto_at) vs ventas 30d.
    const reclAbiertos = (reclamosRes.data || []).filter(r => !r.resuelto_at)
    const reclamosRate = ventas30.length ? reclAbiertos.length / ventas30.length : 0
    // Cancelaciones vs ventas 30d.
    const canceladas = ventas30.filter(v => v.status === 'cancelled').length
    const cancelRate = ventas30.length ? canceladas / ventas30.length : 0
    // Nota: despacho on-time NO se calcula (falta handling_time/deadline de ML — Sprint 7.2).
    setSalud({
      ...saludCuenta({ tiempoResp, reclamosRate, cancelRate }),
      tiempoResp, reclamosRate, cancelRate,
      reclAbiertos: reclAbiertos.length, canceladas, muestra_preguntas: pAns.length,
    })

    // ── Sprint 5.2 · ranking top del mes ──────────────────────────────────
    const nombreMap = {}
    for (const p of prodAllRes.data || []) nombreMap[p.id] = { nombre: p.nombre, marca: p.marca }
    const porProd = {}
    for (const v of ventas30) {
      if (!v.producto_id) continue
      const e = porProd[v.producto_id] || { producto_id: v.producto_id, ventas: 0, unidades: 0, bruto: 0 }
      e.ventas += 1
      e.unidades += Number(v.cantidad || 1)
      e.bruto += Number(v.monto_bruto || 0)
      porProd[v.producto_id] = e
    }
    const rankingArr = Object.values(porProd)
      .map(e => ({ ...e, nombre: nombreMap[e.producto_id]?.nombre || `Producto ${String(e.producto_id).slice(0, 8)}`, marca: nombreMap[e.producto_id]?.marca }))
      .sort((a, b) => b.ventas - a.ventas || b.bruto - a.bruto)
      .slice(0, 10)
    setRanking(rankingArr)

    // ── Sprint 5.3 · publicaciones con error / sync trabado ───────────────
    setPubsError(pubsErrorRes.data || [])

    setLoading(false)
  }

  useEffect(() => { cargar() }, [])

  async function reprocesarPendientes() {
    if (pendientesSync === 0) return
    if (!confirm(`Reintentar la sincronización de ${pendientesSync} publicación${pendientesSync !== 1 ? 'es' : ''} a MercadoLibre?\n\nSi todavía no hay credenciales ML conectadas, quedan en pendiente como hasta ahora.`)) return
    setReprocesando(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ml-reprocesar-pendientes`
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`)
      setMsg({ tipo: data.sincronizadas > 0 ? 'ok' : 'err',
               txt: `${data.sincronizadas || 0} sincronizadas · ${data.shadow || 0} quedaron pendientes · ${data.errores || 0} con error` })
      cargar()
    } catch (e) {
      setMsg({ tipo: 'err', txt: 'Error: ' + e.message })
    } finally {
      setReprocesando(false)
    }
  }

  async function togglePublicar(producto, valor) {
    const { error } = await supabase
      .from('productos')
      .update({ publicar_en_ml: valor })
      .eq('id', producto.id)
    if (error) { setMsg({ tipo: 'err', txt: error.message }); return }
    setMsg({ tipo: 'ok', txt: `${producto.nombre}: ${valor ? 'activado para ML' : 'desactivado de ML'}` })
    cargar()
  }

  async function updateMarkup(producto, nuevoMarkup) {
    const v = nuevoMarkup === '' || nuevoMarkup == null ? null : Number(nuevoMarkup)
    // Markup sobre COSTO: precio_ml = costo × (1 + markup/100). Guardamos el precio, no el %,
    // porque la vista deriva el markup real del precio guardado dinámicamente.
    const costo = Number(producto.costo_unitario || 0)
    if (v == null) {
      const { error } = await supabase.from('productos').update({ ml_precio: null }).eq('id', producto.id)
      if (error) { setMsg({ tipo: 'err', txt: error.message }); return }
    } else if (costo > 0) {
      const nuevoPrecio = Math.round(costo * (1 + v / 100))
      const { error } = await supabase.from('productos').update({ ml_precio: nuevoPrecio }).eq('id', producto.id)
      if (error) { setMsg({ tipo: 'err', txt: error.message }); return }
      setMsg({ tipo: 'ok', txt: `${producto.sku}: markup ${v}% s/costo → precio ML $${nuevoPrecio}` })
    } else {
      setMsg({ tipo: 'err', txt: `${producto.sku} no tiene costo unitario cargado — cargalo primero en la ficha del producto` })
      return
    }
    cargar()
  }

  async function agregarProductoML(productoId) {
    const { error } = await supabase
      .from('productos')
      .update({ publicar_en_ml: true })
      .eq('id', productoId)
    if (error) { setMsg({ tipo: 'err', txt: error.message }); return }
    setMsg({ tipo: 'ok', txt: 'Producto agregado al canal ML. Falta wizard de publicación para subirlo.' })
    setShowAgregar(false)
    cargar()
  }

  // Análisis mercado ML: scrapea precios de competidores por producto usando la extensión
  // Chrome "Limpack Precios". Persiste en productos.ml_stats (mismo formato que
  // analisis_catalogo_items.ml_stats). Fuente y edge fn compartida con Análisis de Catálogo.
  const [analizandoML, setAnalizandoML] = useState(false)
  const [progresoAnalisisML, setProgresoAnalisisML] = useState(null)
  const [extInstalada, setExtInstalada] = useState(false)

  useEffect(() => {
    function onMessage(e) {
      if (e.source !== window) return
      if (e.data?.type === 'LIMPACK_EXTENSION_READY') setExtInstalada(true)
    }
    window.addEventListener('message', onMessage)
    const pings = [0, 300, 800, 2000].map(delay =>
      setTimeout(() => window.postMessage({ type: 'LIMPACK_EXTENSION_CHECK' }, '*'), delay)
    )
    return () => { window.removeEventListener('message', onMessage); pings.forEach(clearTimeout) }
  }, [])

  async function analizarMercadoML() {
    if (!extInstalada) {
      setMsg({ tipo: 'err', txt: 'La extensión "Limpack — Precios" no está instalada. Ver limpack-intranet/extension-precios/README.md' })
      return
    }
    // Productos publicados que faltan stats (o son viejos de más de 7 días)
    const hace7d = Date.now() - 7 * 24 * 3600000
    const candidatos = filtrados.filter(p => {
      if (!p.ml_item_id) return false
      if (!p.ml_stats_actualizado_at) return true
      return new Date(p.ml_stats_actualizado_at).getTime() < hace7d
    })
    if (candidatos.length === 0) {
      setMsg({ tipo: 'info', txt: 'Todos los productos ya tienen análisis reciente (<7 días).' })
      return
    }
    const segundos = Math.ceil(candidatos.length * 8)
    if (!confirm(`¿Analizar mercado ML para ${candidatos.length} productos publicados?\n\nLa extensión abre pestañas ocultas en tu navegador y scrapea el DOM. ~${segundos}s total. Sin costo.`)) return

    setAnalizandoML(true)
    setProgresoAnalisisML({ indice: 0, total: candidatos.length, portal: null, phase: null, nombre: null })
    setMsg({ tipo: 'info', txt: `🔍 Analizando mercado para ${candidatos.length} productos...` })

    // Contrato de la extensión (mismo que usa Análisis de Catálogo):
    // → LIMPACK_BUSCAR_PRECIOS { productos: [{item_id, marca, nombre, presentacion}], portales }
    // ← LIMPACK_ITEM_START, LIMPACK_PROGRESS, LIMPACK_ITEM_LISTO, LIMPACK_BUSQUEDA_LISTA, LIMPACK_BUSQUEDA_ERROR
    const guardadosPendientes = new Set()

    async function onMsg(e) {
      if (e.source !== window) return
      const d = e.data || {}
      if (d.type === 'LIMPACK_ITEM_START') {
        setProgresoAnalisisML({ indice: d.indice, total: d.total, nombre: d.nombre, portal: null, phase: null })
      }
      if (d.type === 'LIMPACK_PROGRESS') {
        setProgresoAnalisisML(p => p ? { ...p, portal: d.portal, phase: d.phase, count: d.count } : null)
      }
      if (d.type === 'LIMPACK_ITEM_LISTO' && d.resultado) {
        // Formato de resultado (ver background.js del extension):
        // { item_id, query_especifico, query_generico, portales: {MercadoLibre:{...}},
        //   stats: {min,max,mediana,promedio,cantidad}, match_exacto, todas_las_publicaciones }
        // Guardamos un subconjunto compacto en productos.ml_stats.
        const r = d.resultado
        const ml = r.portales?.MercadoLibre || {}
        const stats_row = {
          stats: r.stats || null,
          match_exacto: r.match_exacto || null,
          query_especifico: r.query_especifico || null,
          query_generico: r.query_generico || null,
          fuente: ml.fuente || null,
          url_busqueda: ml.url || null,
          publicaciones_muestra: (r.todas_las_publicaciones || []).slice(0, 10),
        }
        const pr = supabase.from('productos').update({
          ml_stats: stats_row,
          ml_stats_actualizado_at: new Date().toISOString(),
        }).eq('id', d.item_id).then(({ error }) => {
          if (error) console.error('guardar productos.ml_stats:', error)
        })
        guardadosPendientes.add(pr)
        pr.finally(() => guardadosPendientes.delete(pr))
      }
      if (d.type === 'LIMPACK_BUSQUEDA_LISTA') {
        window.removeEventListener('message', onMsg)
        await Promise.all([...guardadosPendientes])
        setMsg({ tipo: 'ok', txt: `✅ ${d.resultados?.length || 0} productos analizados` })
        setProgresoAnalisisML(null)
        setAnalizandoML(false)
        cargar()
      }
      if (d.type === 'LIMPACK_BUSQUEDA_ERROR') {
        window.removeEventListener('message', onMsg)
        setMsg({ tipo: 'err', txt: 'Error en extensión: ' + d.error })
        setProgresoAnalisisML(null)
        setAnalizandoML(false)
      }
    }
    window.addEventListener('message', onMsg)

    window.postMessage({
      type: 'LIMPACK_BUSCAR_PRECIOS',
      payload: {
        productos: candidatos.map(p => ({
          item_id: p.id,
          marca: p.marca,
          nombre: p.nombre,
          presentacion: p.presentacion,
        })),
        portales: ['MercadoLibre'],
      },
    }, '*')
  }

  const filtrados = useMemo(() => {
    return productos.filter(p => {
      if (filtro.texto && !(`${p.sku || ''} ${p.nombre || ''} ${p.marca || ''}`.toLowerCase().includes(filtro.texto.toLowerCase()))) return false
      if (filtro.estado === 'activos' && p.ml_status !== 'active') return false
      if (filtro.estado === 'borradores' && p.ml_status !== null && p.ml_status !== 'draft') return false
      if (filtro.estado === 'pausados' && p.ml_status !== 'paused') return false
      if (filtro.estado === 'sin_stock' && Number(p.stock_disponible || 0) > 0) return false
      return true
    })
  }, [productos, filtro])

  const sinPublicar = useMemo(() => {
    return productosAll.filter(p => !p.publicar_en_ml).slice(0, 50)
  }, [productosAll])

  const fmt = (n) => '$ ' + Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 })

  return (
    <AdminLayout title="MercadoLibre">
      <div className="ml-wrap">
        <header className="ml-head">
          <div>
            <div className="ml-eyebrow">CANALES · MERCADOLIBRE</div>
            <h1 className="ml-title">{stats.nickname ? `@${stats.nickname}` : '@LIMPACKUY'}</h1>
            <div className="ml-sub">Ventas, publicaciones y facturación del canal MercadoLibre.</div>
          </div>
          <div className="ml-head-acts">
            <button className="ml-btn-primary" onClick={() => setShowAgregar(true)}>+ Agregar producto a ML</button>
            {stats.credencial_conectada ? (
              <span className="ml-pill ok">● Conectado</span>
            ) : (
              <span className="ml-pill warn">● Sin conectar · falta OAuth</span>
            )}
          </div>
        </header>

        {pendientesSync > 0 && (
          <div style={{
            marginTop: 12, padding: '12px 16px',
            background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 10,
            display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 22 }}>⏳</div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <strong style={{ color: '#78350F', fontSize: 13 }}>
                {pendientesSync} publicación{pendientesSync !== 1 ? 'es' : ''} con cambios sin sincronizar a ML
              </strong>
              <div style={{ fontSize: 11.5, color: '#92400E', marginTop: 2 }}>
                Edits guardados localmente esperando que la cuenta ML esté conectada para empujarse. Reintentá ahora si ya conectaste credenciales.
              </div>
            </div>
            <button
              onClick={reprocesarPendientes}
              disabled={reprocesando}
              style={{
                background: '#F59E0B', color: '#fff', border: 'none',
                padding: '8px 14px', borderRadius: 8,
                fontWeight: 700, fontSize: 12.5,
                cursor: reprocesando ? 'wait' : 'pointer',
                opacity: reprocesando ? 0.7 : 1,
              }}
            >
              {reprocesando ? 'Reintentando…' : `🔄 Reintentar ${pendientesSync}`}
            </button>
          </div>
        )}

        {msg && (
          <div className={`ml-msg ${msg.tipo}`} onClick={() => setMsg(null)}>
            {msg.txt}<span style={{ float: 'right', cursor: 'pointer' }}>×</span>
          </div>
        )}

        {/* Fila 1 · VENTAS */}
        <div className="ml-kpi-group">
          <div className="ml-kpi-title">🛒 VENTAS</div>
          <div className="ml-kpis">
            <KPI lbl="Últimos 7 días" num={fmt(stats.ventas_7d)}
              delta={(() => {
                const d = stats.ventas_7d_prev
                if (d === 0) return `${stats.pedidos_7d} pedidos`
                const pct = ((stats.ventas_7d - d) / d * 100).toFixed(0)
                return `${stats.pedidos_7d} pedidos · ${pct >= 0 ? '↑' : '↓'} ${Math.abs(pct)}% vs 7d prev`
              })()}
              deltaClass={stats.ventas_7d >= stats.ventas_7d_prev ? 'ok' : 'warn'} />
            <KPI lbl="Últimos 30 días" num={fmt(stats.ventas_30d)} delta={`${stats.pedidos_30d} pedidos`} deltaClass="mute" />
            <KPI lbl="Ticket promedio" num={fmt(stats.ticket_30d)} delta="30 días" deltaClass="mute" />
            <KPI lbl="Publicaciones activas" num={stats.items_publicados} delta={stats.pubs_sin_vincular > 0 ? `${stats.pubs_sin_vincular} sin producto vinculado` : 'todas vinculadas'} deltaClass={stats.pubs_sin_vincular > 0 ? 'warn' : 'ok'} />
          </div>
        </div>

        {/* Fila 2 · OPERATIVO */}
        <div className="ml-kpi-group">
          <div className="ml-kpi-title">⚡ OPERATIVO</div>
          <div className="ml-kpis">
            <KPI lbl="Pendientes despacho" num={stats.pendientes_despacho}
              delta={stats.pendientes_despacho > 0 ? '→ ir a Despacho ML' : 'sin backlog'}
              deltaClass={stats.pendientes_despacho > 0 ? 'warn' : 'ok'} />
            <KPI lbl="Preguntas urgentes >2h" num={stats.preguntas_urgentes}
              delta={stats.preguntas_urgentes > 0 ? '⚠ afecta reputación' : 'todo dentro SLA'}
              deltaClass={stats.preguntas_urgentes > 0 ? 'warn' : 'ok'} />
            <KPI lbl="Preguntas pendientes" num={stats.preguntas_pendientes} delta="sin responder" deltaClass={stats.preguntas_pendientes > 0 ? 'warn' : 'ok'} />
            <KPI lbl="CFE sin emitir" num={stats.cfe_pendientes}
              delta={stats.cfe_pendientes > 0 ? 'venta paid sin CFE' : 'todo facturado'}
              deltaClass={stats.cfe_pendientes > 0 ? 'warn' : 'ok'} />
          </div>
        </div>

        {/* Fila 3 · CONTABLE */}
        <div className="ml-kpi-group">
          <div className="ml-kpi-title">📒 CONTABLE (30 días)</div>
          <div className="ml-kpis">
            <KPI lbl="Comisión ML" num={fmt(stats.comision_30d)} delta={stats.ventas_30d ? `511310 · ${(stats.comision_30d / stats.ventas_30d * 100).toFixed(1)}% real` : '511310'} deltaClass="mute" />
            <KPI lbl="Crédito envío (Flex)" num={fmt(stats.credito_envio_30d)} delta="ML te devuelve por repartir" deltaClass="ok" />
            <KPI lbl="Neto ML acumulado" num={fmt(stats.neto_30d)} delta="bruto − comisión + crédito envío" deltaClass="ok" />
            <KPI lbl="% Neto / Bruto" num={stats.ventas_30d ? `${(stats.neto_30d / stats.ventas_30d * 100).toFixed(1)}%` : '—'} delta="rentabilidad canal" deltaClass="mute" />
          </div>
        </div>

        {/* Sprint 5.1 · SALUD DE CUENTA */}
        {salud && (
          <div className="ml-kpi-group">
            <div className="ml-kpi-title">💚 SALUD DE CUENTA ML</div>
            <div className={`ml-salud ml-salud-${salud.color}`}>
              <div className="ml-salud-badge">
                <span className="ml-salud-dot" />
                {salud.color === 'verde' ? 'Saludable' : salud.color === 'amarillo' ? 'Atención' : 'Riesgo'}
              </div>
              <div className="ml-salud-metrics">
                <div>
                  <span className="ml-salud-m-lbl">Tiempo respuesta</span>
                  <span className="ml-salud-m-val">{salud.tiempoResp == null ? '—' : `${salud.tiempoResp.toFixed(1)}h`}</span>
                  <span className="ml-salud-m-sub">objetivo &lt;4h · {salud.muestra_preguntas} pregs 30d</span>
                </div>
                <div>
                  <span className="ml-salud-m-lbl">Reclamos abiertos</span>
                  <span className="ml-salud-m-val">{salud.reclAbiertos}</span>
                  <span className="ml-salud-m-sub">{(salud.reclamosRate * 100).toFixed(1)}% vs ventas · obj &lt;2%</span>
                </div>
                <div>
                  <span className="ml-salud-m-lbl">Cancelaciones 30d</span>
                  <span className="ml-salud-m-val">{salud.canceladas}</span>
                  <span className="ml-salud-m-sub">{(salud.cancelRate * 100).toFixed(1)}% vs ventas · obj &lt;5%</span>
                </div>
                <div>
                  <span className="ml-salud-m-lbl">Despacho on-time</span>
                  <span className="ml-salud-m-val" style={{ color: '#B0AEA6' }}>—</span>
                  <span className="ml-salud-m-sub">sin datos (falta deadline ML · Sprint 7.2)</span>
                </div>
              </div>
              {salud.alertas.length > 0 && (
                <ul className="ml-salud-alertas">
                  {salud.alertas.map((a, i) => <li key={i} className={a.nivel === 'bad' ? 'bad' : 'warn'}>⚠ {a.texto}</li>)}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Sprint 5.3 · PUBLICACIONES CON ERROR */}
        {pubsError.length > 0 && (
          <div className="ml-card ml-card-alert">
            <h3>
              🚨 {pubsError.length} publicación{pubsError.length !== 1 ? 'es' : ''} con problema de sincronización
              <button className="ml-btn-primary" onClick={reprocesarPendientes} disabled={reprocesando} style={{ padding: '6px 12px' }}>
                {reprocesando ? 'Reintentando…' : '🔄 Reintentar todas'}
              </button>
            </h3>
            <div style={{ display: 'grid', gap: 6 }}>
              {pubsError.map(p => (
                <div key={p.id} className="ml-err-row">
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: '#1A3461' }}>
                    {p.titulo || 'Sin título'} <span className="ml-mono">· {p.status || 'error'}</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#791F1F', marginTop: 1 }}>
                    {p.pendiente_sync_motivo || 'error de publicación'}
                    {p.pendiente_sync_desde ? ` · trabado desde ${new Date(p.pendiente_sync_desde).toLocaleDateString('es-UY')}` : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sprint 5.2 · TOP DEL MES */}
        {ranking.length > 0 && (
          <div className="ml-card">
            <h3>🏆 Top del mes <span className="ml-link" style={{ cursor: 'default', color: '#888780' }}>por ventas · últimos 30 días</span></h3>
            <div className="ml-table-wrap" style={{ border: 0 }}>
              <table className="ml-rank">
                <thead>
                  <tr><th>#</th><th>Producto</th><th style={{ textAlign: 'right' }}>Ventas</th><th style={{ textAlign: 'right' }}>Unidades</th><th style={{ textAlign: 'right' }}>Facturación</th></tr>
                </thead>
                <tbody>
                  {ranking.map((r, i) => (
                    <tr key={r.producto_id}>
                      <td>{i + 1}</td>
                      <td>{r.nombre}{r.marca ? <span className="ml-mono"> · {r.marca}</span> : null}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.ventas}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.unidades}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmt(r.bruto)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 10.5, color: '#888780', marginTop: 6 }}>Ranking por conversión disponible cuando ML devuelva visitas por publicación (Sprint 10.1).</div>
          </div>
        )}

        {showAgregar && (
          <div className="ml-card">
            <h3>Agregar producto al canal ML <span className="ml-link" onClick={() => setShowAgregar(false)}>cancelar</span></h3>
            <div style={{ fontSize: 12, color: '#5F5E5A', marginBottom: 10 }}>
              Buscá el producto del catálogo y marcalo como "publicar en ML". Después abrís el wizard para definir título, fotos y publicar.
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #E6E2D8', borderRadius: 8 }}>
              {sinPublicar.map(p => (
                <div key={p.id} className="ml-prod-pick" onClick={() => agregarProductoML(p.id)}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{p.nombre}</div>
                    <div style={{ fontSize: 11, color: '#5F5E5A' }}>{p.sku} · {p.marca} {p.presentacion ? `· ${p.presentacion}` : ''}</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#1A3461' }}>{fmt(p.precio_publico)}</div>
                </div>
              ))}
              {sinPublicar.length === 0 && <div style={{ padding: 14, fontSize: 12, color: '#888780' }}>Todos los productos del catálogo ya están marcados para ML.</div>}
            </div>
          </div>
        )}

        <div className="ml-filter-row">
          <input
            type="text"
            placeholder="Buscar SKU, nombre, marca…"
            value={filtro.texto}
            onChange={e => setFiltro(f => ({ ...f, texto: e.target.value }))}
            className="ml-input"
          />
          <select value={filtro.estado} onChange={e => setFiltro(f => ({ ...f, estado: e.target.value }))} className="ml-input">
            <option value="todos">Todos los estados</option>
            <option value="activos">Activos en ML</option>
            <option value="borradores">Borradores (no publicados)</option>
            <option value="pausados">Pausados</option>
            <option value="sin_stock">Sin stock</option>
          </select>
          <div style={{ flex: 1 }} />
          <button
            onClick={analizarMercadoML}
            disabled={analizandoML || !extInstalada}
            title={extInstalada ? 'Scrapea precios de competidores en ML para cada producto publicado. Reusa la extensión "Limpack — Precios".' : 'Instalá la extensión "Limpack — Precios" para usar esta función'}
            style={{
              padding: '8px 14px',
              border: '1px solid #1A3461',
              background: analizandoML ? '#eee' : (extInstalada ? '#1A3461' : '#ccc'),
              color: extInstalada ? '#fff' : '#666',
              borderRadius: 6,
              fontSize: 12.5,
              fontWeight: 600,
              cursor: (analizandoML || !extInstalada) ? 'not-allowed' : 'pointer',
            }}
          >
            {analizandoML && progresoAnalisisML
              ? `🔍 Analizando ${progresoAnalisisML.indice}/${progresoAnalisisML.total}...`
              : '🔍 Analizar mercado ML'}
          </button>
          <span style={{ fontSize: 12, color: '#888780' }}>{filtrados.length} producto(s)</span>
        </div>

        <div className="ml-info-box">
          <strong>Estrategia markup:</strong> comisión ML <b>{config.comision_pct}%</b> + costo fijo según precio: <b>≤$500 → $15</b> · <b>$500-$750 → $25</b> · <b>$750-$1000 → $40</b> · <b>≥$1000 → sin fijo</b>. Para anclaje real: markup ≥ comisión + 10% (~{(config.comision_pct + 10).toFixed(1)}%).
        </div>

        {analizandoML && progresoAnalisisML && (
          <div style={{ padding: '10px 14px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 6, marginBottom: 12, fontSize: 12.5, color: '#1E3A8A' }}>
            <strong>🔍 Scrapeando ML:</strong> {progresoAnalisisML.indice}/{progresoAnalisisML.total}
            {progresoAnalisisML.nombre && <> · último: <em>{progresoAnalisisML.nombre}</em></>}
          </div>
        )}

        <div className="ml-table-wrap">
          <table className="ml-table">
            <thead>
              <tr>
                <th style={{ width: '4%' }}></th>
                <th style={{ width: '22%' }}>Producto</th>
                <th>Costo</th>
                <th>Precio web</th>
                <th title="% sobre costo unitario">Markup s/costo</th>
                <th>Precio ML</th>
                <th>Comisión</th>
                <th>Neto Limpack</th>
                <th title="Precios de competidores scrapeados por la extensión">Mercado ML</th>
                <th>Stock</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} style={{ textAlign: 'center', padding: 30, color: '#888780' }}>Cargando…</td></tr>
              ) : filtrados.length === 0 ? (
                <tr><td colSpan={12} style={{ textAlign: 'center', padding: 30, color: '#888780' }}>
                  No hay productos. Empezá agregando uno con el botón <strong>+ Agregar producto a ML</strong> arriba.
                </td></tr>
              ) : filtrados.map(p => (
                <tr key={p.id}>
                  <td>
                    <Toggle activo={p.publicar_en_ml} onChange={(v) => togglePublicar(p, v)} />
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, color: '#1A3461' }}>{p.nombre}</div>
                    <div style={{ fontSize: 10.5, color: '#888780', marginTop: 1 }}>
                      {p.sku} {p.marca ? `· ${p.marca}` : ''} {p.presentacion ? `· ${p.presentacion}` : ''}
                    </div>
                  </td>
                  <td style={{ fontSize: 11.5, color: '#888780' }}>{fmt(p.costo_unitario)}</td>
                  <td>{fmt(p.precio_web)}</td>
                  <td>
                    {(() => {
                      const markupSobreWeb = Number(p.precio_web || 0) > 0
                        ? ((Number(p.precio_ml || 0) / Number(p.precio_web)) - 1) * 100
                        : null
                      return (
                        <div>
                          <input
                            type="number"
                            className="ml-markup-input"
                            defaultValue={p.markup_pct ?? ''}
                            placeholder="s/costo"
                            onBlur={(e) => updateMarkup(p, e.target.value)}
                          /> %
                          {markupSobreWeb !== null && (
                            <div style={{ fontSize: 10, color: '#888780', marginTop: 2 }}>
                              vs web: {markupSobreWeb >= 0 ? '+' : ''}{markupSobreWeb.toFixed(0)}%
                            </div>
                          )}
                        </div>
                      )
                    })()}
                  </td>
                  <td><strong>{fmt(p.precio_ml)}</strong></td>
                  <td style={{ fontSize: 11.5 }}>
                    {fmt(p.comision_ml_estimada)}
                    {Number(p.costo_fijo_estimado || 0) > 0 && <div style={{ fontSize: 10, color: '#888780' }}>+${p.costo_fijo_estimado} fijo</div>}
                  </td>
                  <td>
                    {(() => {
                      const neto = Number(p.precio_ml || 0) - Number(p.comision_ml_estimada || 0) - Number(p.costo_fijo_estimado || 0)
                      const dif = neto - Number(p.precio_web || 0)
                      return (
                        <div>
                          <div style={{ color: dif >= 0 ? '#1D9E75' : '#C53030', fontWeight: 600 }}>{fmt(neto)}</div>
                          <div style={{ fontSize: 10, color: '#888780' }}>
                            {dif >= 0 ? '+' : ''}{fmt(dif)} vs web
                          </div>
                        </div>
                      )
                    })()}
                  </td>
                  <td style={{ fontSize: 11 }}>
                    {(() => {
                      const s = p.ml_stats?.stats
                      if (!s || !s.cantidad) {
                        return <span style={{ color: '#B8B4A8', fontStyle: 'italic' }}>—</span>
                      }
                      const min = Number(s.min || 0)
                      const max = Number(s.max || 0)
                      const med = Number(s.mediana || 0)
                      const nuestro = Number(p.precio_ml || 0)
                      // Diff vs mediana (%): negativo = más barato que mediana → mejor anclaje
                      const diffPct = med > 0 ? ((nuestro - med) / med) * 100 : 0
                      const posColor = diffPct < -5 ? '#1D9E75' : diffPct > 15 ? '#C53030' : '#B87500'
                      const ageDays = p.ml_stats_actualizado_at
                        ? Math.floor((Date.now() - new Date(p.ml_stats_actualizado_at).getTime()) / 86400000)
                        : null
                      return (
                        <div>
                          <div style={{ fontWeight: 600, color: '#1A3461' }}>
                            {fmt(min)} · <span style={{ color: '#1D9E75' }}>{fmt(med)}</span> · {fmt(max)}
                          </div>
                          <div style={{ fontSize: 10, color: posColor, marginTop: 1 }}>
                            {diffPct >= 0 ? '+' : ''}{diffPct.toFixed(0)}% vs mediana · n={s.cantidad}
                          </div>
                          {ageDays !== null && (
                            <div style={{ fontSize: 9.5, color: '#B8B4A8' }}>
                              {ageDays === 0 ? 'hoy' : `hace ${ageDays}d`}
                            </div>
                          )}
                        </div>
                      )
                    })()}
                  </td>
                  <td>{Number(p.stock_disponible || 0)}</td>
                  <td>
                    <EstadoPill status={p.ml_status} stock={Number(p.stock_disponible || 0)} />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 4 }}>
                      <Link
                        to={`/admin/canales/mercadolibre/publicar/${p.id}`}
                        className="ml-row-act"
                        title={p.ml_status === 'active' ? 'Editar publicación' : p.ml_status === 'draft' ? 'Continuar draft' : 'Publicar (wizard)'}
                        style={{ textDecoration: 'none' }}
                      >
                        {p.ml_status === 'active' ? '✎' : '⚙'}
                      </Link>
                      {p.ml_item_id ? (
                        <a href={`https://articulo.mercadolibre.com.uy/${p.ml_item_id}`} target="_blank" rel="noreferrer" title="Ver en ML" className="ml-row-act">↗</a>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Estilos />
    </AdminLayout>
  )
}

// Sprint 5.1 — semáforo de salud de cuenta ML (verde/amarillo/rojo) según umbrales ML.
// despachoOnTime se omite hasta tener el deadline de ML (handling_time) — Sprint 7.2.
function saludCuenta({ tiempoResp, reclamosRate, cancelRate }) {
  const alertas = []
  if (tiempoResp != null && tiempoResp > 8)      alertas.push({ nivel: 'bad',  texto: `Tiempo de respuesta ${tiempoResp.toFixed(1)}h (>8h)` })
  else if (tiempoResp != null && tiempoResp > 4) alertas.push({ nivel: 'warn', texto: `Tiempo de respuesta ${tiempoResp.toFixed(1)}h (>4h)` })
  if (reclamosRate > 0.02) alertas.push({ nivel: 'warn', texto: `Reclamos abiertos ${(reclamosRate * 100).toFixed(1)}% (>2%)` })
  if (cancelRate > 0.05)   alertas.push({ nivel: 'bad',  texto: `Cancelaciones ${(cancelRate * 100).toFixed(1)}% (>5%)` })
  if (alertas.some(a => a.nivel === 'bad'))  return { color: 'rojo', alertas }
  if (alertas.some(a => a.nivel === 'warn')) return { color: 'amarillo', alertas }
  return { color: 'verde', alertas: [] }
}

function KPI({ lbl, num, delta, deltaClass = '' }) {
  return (
    <div className="ml-kpi">
      <div className="ml-kpi-lbl">{lbl}</div>
      <div className="ml-kpi-num">{num}</div>
      <div className={`ml-kpi-delta ${deltaClass}`}>{delta}</div>
    </div>
  )
}

function Toggle({ activo, onChange }) {
  return (
    <span
      className={`ml-toggle ${activo ? 'on' : 'off'}`}
      onClick={() => onChange(!activo)}
      title={activo ? 'Activado para ML — click para desactivar' : 'Desactivado — click para activar'}
    />
  )
}

function EstadoPill({ status, stock }) {
  if (!status) return <span className="ml-state-pill borrador">Borrador</span>
  if (status === 'active' && stock === 0) return <span className="ml-state-pill sin-stock">Sin stock</span>
  if (status === 'active') return <span className="ml-state-pill activo">Activo</span>
  if (status === 'paused') return <span className="ml-state-pill pausado">Pausado</span>
  if (status === 'under_review') return <span className="ml-state-pill revision">En revisión</span>
  if (status === 'closed') return <span className="ml-state-pill cerrado">Cerrado</span>
  if (status === 'rejected') return <span className="ml-state-pill rechazado">Rechazado</span>
  return <span className="ml-state-pill">{status}</span>
}

function Estilos() {
  return (
    <style>{`
      .ml-wrap { max-width: 1500px; margin: 0 auto; padding: 0 4px 60px; }
      .ml-head { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 14px 18px; margin-bottom: 12px; display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
      .ml-eyebrow { font-size: 10.5px; letter-spacing: 0.8px; color: #888780; text-transform: uppercase; font-weight: 600; }
      .ml-title { margin: 4px 0; font-size: 22px; font-weight: 600; color: #1A3461; }
      .ml-sub { font-size: 12.5px; color: #5F5E5A; }
      .ml-head-acts { display: flex; flex-direction: column; gap: 6px; align-items: flex-end; }
      .ml-btn-primary { background: #00A8B5; color: #fff; border: 0; padding: 8px 14px; border-radius: 8px; font-size: 12.5px; font-weight: 600; cursor: pointer; font-family: inherit; }
      .ml-btn-ghost { background: #fff; color: #1A3461; border: 1px solid #E6E2D8; padding: 8px 14px; border-radius: 8px; font-size: 12.5px; cursor: pointer; font-family: inherit; text-decoration: none; }
      .ml-pill { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; }
      .ml-pill.ok { background: #E1F5EE; color: #085041; }
      .ml-pill.warn { background: #FAEEDA; color: #633806; }

      .ml-msg { padding: 9px 12px; border-radius: 8px; margin-bottom: 12px; font-size: 12.5px; cursor: pointer; }
      .ml-msg.ok { background: #E1F5EE; border: 1px solid #1D9E75; color: #085041; }
      .ml-msg.err { background: #FDEEEE; border: 1px solid #C53030; color: #791F1F; }

      .ml-kpi-group { margin-bottom: 12px; }
      .ml-kpi-title { font-size: 10.5px; letter-spacing: 0.8px; color: #888780; text-transform: uppercase; font-weight: 700; margin-bottom: 6px; padding-left: 2px; }
      .ml-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
      .ml-kpi { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 13px 16px; }
      .ml-kpi-lbl { font-size: 10px; letter-spacing: 0.8px; color: #888780; text-transform: uppercase; font-weight: 600; margin-bottom: 5px; }
      .ml-kpi-num { font-size: 24px; font-weight: 700; color: #1A3461; line-height: 1; }
      .ml-kpi-delta { font-size: 11px; color: #1D9E75; margin-top: 5px; font-weight: 500; }
      .ml-kpi-delta.ok { color: #1D9E75; }
      .ml-kpi-delta.warn { color: #BA7517; }
      .ml-kpi-delta.mute { color: #888780; font-weight: 400; }

      .ml-card { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 14px 16px; margin-bottom: 12px; }
      .ml-card h3 { margin: 0 0 8px; font-size: 13px; font-weight: 600; color: #1A3461; display: flex; align-items: center; justify-content: space-between; }
      .ml-link { font-size: 11.5px; color: #00A8B5; cursor: pointer; font-weight: 500; }

      .ml-prod-pick { padding: 10px 12px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #F1EFE8; cursor: pointer; }
      .ml-prod-pick:hover { background: #F8F6F1; }
      .ml-prod-pick:last-of-type { border-bottom: 0; }

      .ml-filter-row { display: flex; gap: 8px; align-items: center; margin-bottom: 12px; flex-wrap: wrap; }
      .ml-input { padding: 7px 10px; border: 1px solid #E6E2D8; border-radius: 6px; font-size: 12.5px; font-family: inherit; color: #1A3461; background: #fff; }
      .ml-filter-row .ml-input:first-child { min-width: 240px; }

      .ml-info-box { background: #FFFBEF; border: 1px solid #E6D9B8; border-radius: 8px; padding: 10px 14px; font-size: 12px; color: #1A3461; line-height: 1.5; margin-bottom: 12px; }
      .ml-info-box strong { color: #C5A059; }

      .ml-table-wrap { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; overflow: hidden; }
      .ml-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
      .ml-table thead th { text-align: left; padding: 9px 12px; background: #F8F6F1; font-size: 10.5px; letter-spacing: 0.5px; text-transform: uppercase; color: #888780; font-weight: 600; border-bottom: 1px solid #E6E2D8; }
      .ml-table tbody td { padding: 10px 12px; border-bottom: 1px solid #F1EFE8; vertical-align: middle; }
      .ml-table tbody tr:hover { background: #F8F6F1; }

      .ml-toggle { display: inline-block; width: 30px; height: 16px; border-radius: 999px; position: relative; cursor: pointer; transition: background .15s; }
      .ml-toggle.on  { background: #00A8B5; }
      .ml-toggle.off { background: #C0BFB8; }
      .ml-toggle::after { content: ''; position: absolute; top: 2px; width: 12px; height: 12px; border-radius: 50%; background: white; transition: left .15s; }
      .ml-toggle.on::after  { left: 16px; }
      .ml-toggle.off::after { left: 2px; }

      .ml-markup-input { width: 52px; padding: 4px 6px; border: 1px solid #E6E2D8; border-radius: 5px; font-size: 12px; text-align: center; font-family: inherit; color: #1A3461; }

      .ml-state-pill { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 10.5px; font-weight: 600; }
      .ml-state-pill.activo { background: #E1F5EE; color: #085041; }
      .ml-state-pill.sin-stock { background: #FDEEEE; color: #791F1F; }
      .ml-state-pill.borrador { background: #F8F6F1; color: #5F5E5A; }
      .ml-state-pill.pausado { background: #FAEEDA; color: #633806; }
      .ml-state-pill.revision { background: #EEEDFE; color: #534AB7; }
      .ml-state-pill.cerrado { background: #F1EFE8; color: #5F5E5A; }
      .ml-state-pill.rechazado { background: #FDEEEE; color: #791F1F; }

      .ml-row-act { background: #fff; border: 1px solid #E6E2D8; padding: 3px 8px; border-radius: 5px; font-size: 12px; text-decoration: none; color: #1A3461; }
      .ml-row-act.dim { color: #C0BFB8; cursor: default; }

      /* Sprint 5 — salud, errores, ranking */
      .ml-salud { background: #fff; border: 1px solid #E6E2D8; border-left-width: 5px; border-radius: 10px; padding: 14px 16px; display: flex; flex-wrap: wrap; align-items: center; gap: 18px; }
      .ml-salud-verde    { border-left-color: #1D9E75; }
      .ml-salud-amarillo { border-left-color: #F59E0B; }
      .ml-salud-rojo     { border-left-color: #C53030; }
      .ml-salud-badge { display: inline-flex; align-items: center; gap: 8px; font-weight: 700; font-size: 15px; min-width: 110px; }
      .ml-salud-verde .ml-salud-badge    { color: #085041; }
      .ml-salud-amarillo .ml-salud-badge { color: #633806; }
      .ml-salud-rojo .ml-salud-badge     { color: #791F1F; }
      .ml-salud-dot { width: 12px; height: 12px; border-radius: 50%; display: inline-block; }
      .ml-salud-verde .ml-salud-dot    { background: #1D9E75; }
      .ml-salud-amarillo .ml-salud-dot { background: #F59E0B; }
      .ml-salud-rojo .ml-salud-dot     { background: #C53030; }
      .ml-salud-metrics { display: flex; flex-wrap: wrap; gap: 22px; flex: 1; }
      .ml-salud-metrics > div { display: flex; flex-direction: column; gap: 1px; }
      .ml-salud-m-lbl { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #888780; font-weight: 600; }
      .ml-salud-m-val { font-size: 18px; font-weight: 700; color: #1A3461; line-height: 1.1; font-variant-numeric: tabular-nums; }
      .ml-salud-m-sub { font-size: 10px; color: #888780; }
      .ml-salud-alertas { list-style: none; margin: 2px 0 0; padding: 0; width: 100%; display: flex; flex-direction: column; gap: 3px; }
      .ml-salud-alertas li { font-size: 11.5px; font-weight: 600; }
      .ml-salud-alertas li.warn { color: #BA7517; }
      .ml-salud-alertas li.bad  { color: #C53030; }

      .ml-card-alert { border-color: #F5C6C6; background: #FFF7F7; }
      .ml-card-alert h3 { color: #791F1F; }
      .ml-err-row { padding: 7px 10px; border: 1px solid #F1EFE8; border-radius: 6px; background: #fff; }

      .ml-rank { width: 100%; border-collapse: collapse; font-size: 12.5px; }
      .ml-rank th { text-align: left; padding: 6px 10px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #888780; font-weight: 600; border-bottom: 1px solid #E6E2D8; }
      .ml-rank td { padding: 7px 10px; border-bottom: 1px solid #F1EFE8; color: #1A3461; }
      .ml-rank tr:last-child td { border-bottom: 0; }
      .ml-rank td:first-child { font-weight: 700; color: #B0AEA6; width: 28px; }
      .ml-mono { font-family: ui-monospace, Menlo, monospace; font-size: 10.5px; color: #888780; font-weight: 400; }
    `}</style>
  )
}
